// Import a B2B invoice by dropping the customer PO / invoice PDF.
//
// Flow (all frontend — reuses the deployed extract-po Lambda + the already
// write-whitelisted b2b_invoices / b2b_invoice_lines tables):
//   1. Drop a PDF/PNG/JPEG → fileToBase64 → POST extract-po (kind 'po')
//   2. AI returns supplier / po_number / date / currency / total / line_items
//   3. Pre-fill a CONFIRM form: fuzzy-match the customer, map line items to
//      editable rows (Ivy picks the SKU per line — the PO carries qty +
//      description but no SKU)
//   4. On confirm: insert b2b_invoices + b2b_invoice_lines + a 'draft'
//      activity note. Approval still happens in Asana; this just lands the
//      record + units cleanly.
//
// Deliberately does NOT persist the PDF itself — keeping the file on record
// (attachments table + storage) is the separate backend follow-up.

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import { fileToBase64 } from '../lib/fileEncoding';
import { computeDueDate, gstSplit, fmtMoney, fmtDate, TERMS, CURRENCIES, FULFILMENT, INVOICE_STATUSES } from './b2bLib';

const todayISO = () => new Date().toISOString().slice(0, 10);
const ACCEPTED = 'application/pdf,image/png,image/jpeg';
const rk = () => Math.random().toString(36).slice(2, 8);

// Best-effort match of the extracted company name to a known B2B customer.
// The PO's "supplier"/header company is the buyer (our customer); the match
// is only a suggestion — the customer field stays a required, editable pick.
function matchCustomer(supplier, customers) {
  if (!supplier) return '';
  const s = supplier.toLowerCase().trim();
  const c = customers.find(x => (x.name || '').toLowerCase() === s)
    || customers.find(x => { const n = (x.name || '').toLowerCase(); return n.length > 2 && (s.includes(n) || n.includes(s)); });
  return c?.customer_id || '';
}

export default function ImportInvoiceModal({ customers, session, onClose, onCreated }) {
  const toast = useToast();
  const [phase, setPhase] = useState('drop');   // drop | extracting | confirm
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [matched, setMatched] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [f, setF] = useState({
    customer_id: '', invoice_ref: '', po_ref: '', sales_order_ref: '', skutopia_url: '',
    issue_date: todayISO(), terms: '30 Days', currency: 'AUD',
    amount_inc_gst: '', status: 'Draft', fulfilment_status: 'Not Sent', eta: '',
  });
  const [lines, setLines] = useState([]);

  useEffect(() => {
    aws.from('forecaster_catalog').select('id,sku_name,sku_code').eq('is_active', true).neq('status', 'retired').order('sku_name')
      .then(({ data }) => setCatalog(data || []));
  }, []);

  const cust = useMemo(() => customers.find(c => c.customer_id === f.customer_id) || null, [customers, f.customer_id]);
  const inc = f.amount_inc_gst === '' ? null : Number(f.amount_inc_gst);
  const due = f.issue_date && f.terms ? computeDueDate(f.issue_date, f.terms) : null;
  const split = inc == null ? null : gstSplit(inc, f.currency);
  const lineUnits = lines.reduce((s, l) => s + Number(l.qty || 0), 0);
  const lineValue = lines.reduce((s, l) => s + (l.unit_price === '' ? 0 : Number(l.qty || 0) * Number(l.unit_price || 0)), 0);

  async function onFile(file) {
    if (!file) return;
    setError(''); setPhase('extracting');
    try {
      const base64 = await fileToBase64(file);
      const res = await fetch(import.meta.env.VITE_AWS_EXTRACT_PO_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: base64, mime_type: file.type, userPin: session?.code }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setError(e.error || 'Extraction failed'); setPhase('drop'); return; }
      const d = await res.json();
      const matchedId = matchCustomer(d.supplier, customers);
      const mc = customers.find(c => c.customer_id === matchedId) || null;
      const cur = CURRENCIES.includes(d.currency) ? d.currency : (mc?.currency || 'AUD');
      setMatched(!!matchedId);
      setF(s => ({
        ...s,
        customer_id: matchedId,
        po_ref: d.po_number ? String(d.po_number) : '',
        issue_date: d.date_issued || s.issue_date,
        currency: cur,
        terms: mc?.default_terms || s.terms,
        amount_inc_gst: d.total != null ? String(d.total) : '',
      }));
      setLines(Array.isArray(d.line_items) ? d.line_items.map(li => ({
        _k: rk(), sku: '', description: li.description || '',
        qty: li.qty != null ? String(li.qty) : '', unit_price: li.unit_cost != null ? String(li.unit_cost) : '',
      })) : []);
      setPhase('confirm');
    } catch (err) {
      setError(err?.message || 'Extraction failed'); setPhase('drop');
    }
  }

  const pickCustomer = (id) => {
    const c = customers.find(x => x.customer_id === id);
    setMatched(false);
    setF(s => ({ ...s, customer_id: id, currency: c?.currency || s.currency, terms: c?.default_terms || s.terms }));
  };
  const setLine = (k, patch) => setLines(ls => ls.map(l => l._k === k ? { ...l, ...patch } : l));
  const addRow = () => setLines(ls => [...ls, { _k: rk(), sku: '', description: '', qty: '', unit_price: '' }]);
  const delRow = (k) => setLines(ls => ls.filter(l => l._k !== k));
  const pickSku = (k, code) => {
    const c = catalog.find(x => x.sku_code === code);
    const cur = lines.find(l => l._k === k);
    setLine(k, { sku: code, description: (cur?.description) || c?.sku_name || '' });
  };

  const save = async () => {
    if (!f.customer_id) { toast.error('Pick a customer', 'A B2B invoice must belong to a customer.'); return; }
    setBusy(true);
    try {
      const rows = await authWrite(session, 'b2b_invoices', 'insert', {
        customer_id: f.customer_id, invoice_ref: f.invoice_ref || null, po_ref: f.po_ref || null,
        sales_order_ref: f.sales_order_ref || null, skutopia_url: f.skutopia_url || null,
        issue_date: f.issue_date || null, due_date: due, terms: f.terms || null, currency: f.currency,
        amount_inc_gst: inc, amount_ex_gst: split ? split.ex : null, gst: split ? split.gst : null,
        status: f.status, fulfilment_status: f.fulfilment_status, eta: f.eta || null,
        created_by: session?.code || null,
      });
      const id = Array.isArray(rows) && rows[0] ? rows[0].id : null;
      if (!id) throw new Error('No invoice id returned');
      let saved = 0;
      for (const l of lines) {
        const qty = Number(l.qty);
        if (!qty || qty <= 0) continue;
        const price = l.unit_price === '' ? null : Number(l.unit_price);
        await authWrite(session, 'b2b_invoice_lines', 'insert', {
          invoice_id: id, sku: l.sku || null, description: l.description.trim() || null,
          qty, unit_price: price, line_total: price == null ? null : Math.round(qty * price * 100) / 100,
        });
        saved++;
      }
      await authWrite(session, 'b2b_activity', 'insert', {
        invoice_id: id, occurred_at: new Date().toISOString(), actor: session?.name || session?.code || 'staff',
        type: 'draft', body: `Imported from PO${f.po_ref ? ` ${f.po_ref}` : ''} (${cust?.name || ''}) · ${saved} line${saved === 1 ? '' : 's'}`,
      });
      toast.success('Invoice imported');
      onCreated && onCreated(id);
    } catch (e) {
      const { title, body } = humanizeError(e, 'Could not import invoice'); toast.error(title, body); setBusy(false);
    }
  };

  return createPortal(
    <>
      <div onClick={() => !busy && phase !== 'extracting' && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 220, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 221, width: 640, maxWidth: '95vw', maxHeight: '90vh', background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid #e4e4e7', fontSize: 16, fontWeight: 700 }}>
          Import invoice from PO / PDF
        </div>

        {/* ── drop / extracting ── */}
        {phase !== 'confirm' ? (
          <div style={{ padding: 22 }}>
            <label
              onDragOver={e => { e.preventDefault(); }}
              onDrop={e => { e.preventDefault(); if (phase !== 'extracting') onFile(e.dataTransfer?.files?.[0]); }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, height: 200, border: '2px dashed #d4d4d8', borderRadius: 12, cursor: phase === 'extracting' ? 'default' : 'pointer', background: '#fafafa', textAlign: 'center', padding: 20 }}>
              {phase === 'extracting' ? (
                <div style={{ fontSize: 13, color: '#52525b' }}>Reading the document with AI…</div>
              ) : (
                <>
                  <div style={{ fontSize: 28 }}>📄</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0a0a0a' }}>Drop the PO / invoice here, or click to choose</div>
                  <div style={{ fontSize: 11, color: '#a1a1aa' }}>PDF, PNG or JPEG · the AI pre-fills the invoice, you confirm before saving</div>
                  <input type="file" accept={ACCEPTED} hidden onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; onFile(file); }} />
                </>
              )}
            </label>
            {error && <div style={{ marginTop: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" disabled={phase === 'extracting'} onClick={onClose}>Cancel</button>
            </div>
          </div>
        ) : (
          /* ── confirm ── */
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
              <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 12 }}>
                Extracted by AI — check the <b>customer</b> and pick a <b>SKU</b> for each line before saving.
              </div>

              <div className="form-group">
                <label className="form-label">Customer {matched && <span style={{ color: '#16a34a', fontSize: 11 }}>· auto-matched</span>}</label>
                <select className="form-select" value={f.customer_id} onChange={e => pickCustomer(e.target.value)} style={!f.customer_id ? { borderColor: '#fca5a5' } : undefined}>
                  <option value="">Select customer…</option>
                  {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name} ({c.currency})</option>)}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FG label="Invoice ref"><input className="form-input" value={f.invoice_ref} onChange={e => setF(s => ({ ...s, invoice_ref: e.target.value }))} /></FG>
                <FG label="PO ref"><input className="form-input" value={f.po_ref} onChange={e => setF(s => ({ ...s, po_ref: e.target.value }))} /></FG>
                <FG label="Issue date"><input type="date" className="form-input" value={f.issue_date} onChange={e => setF(s => ({ ...s, issue_date: e.target.value }))} /></FG>
                <FG label="Terms"><select className="form-select" value={f.terms} onChange={e => setF(s => ({ ...s, terms: e.target.value }))}>{TERMS.map(t => <option key={t}>{t}</option>)}</select></FG>
                <FG label="Currency"><select className="form-select" value={f.currency} onChange={e => setF(s => ({ ...s, currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></FG>
                <FG label="Amount inc GST"><input type="number" step="0.01" className="form-input" value={f.amount_inc_gst} onChange={e => setF(s => ({ ...s, amount_inc_gst: e.target.value }))} /></FG>
                <FG label="Status"><select className="form-select" value={f.status} onChange={e => setF(s => ({ ...s, status: e.target.value }))}>{INVOICE_STATUSES.filter(x => x !== 'Cancelled').map(x => <option key={x}>{x}</option>)}</select></FG>
                <FG label="Fulfilment"><select className="form-select" value={f.fulfilment_status} onChange={e => setF(s => ({ ...s, fulfilment_status: e.target.value }))}>{FULFILMENT.map(x => <option key={x}>{x}</option>)}</select></FG>
              </div>
              <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 6, marginBottom: 14 }}>
                Due {due ? fmtDate(due) : '—'}{split ? ` · GST ${fmtMoney(split.gst, f.currency)} · ex ${fmtMoney(split.ex, f.currency)}` : ''}{f.currency !== 'AUD' ? ' · export, GST-free' : ''}
              </div>

              {/* line items */}
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0a0a0a', marginBottom: 8 }}>
                Line items ({lines.length}) · {lineUnits} units{lineValue ? ` · ${fmtMoney(lineValue, f.currency)}` : ''}
              </div>
              {lines.map(l => (
                <div key={l._k} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  <select value={l.sku} onChange={e => pickSku(l._k, e.target.value)} style={mini(140, !l.sku)}>
                    <option value="">SKU…</option>
                    {catalog.map(c => <option key={c.id} value={c.sku_code}>{c.sku_code} — {c.sku_name}</option>)}
                  </select>
                  <input placeholder="Description" value={l.description} onChange={e => setLine(l._k, { description: e.target.value })} style={{ ...mini(0), flex: 1, minWidth: 90 }} />
                  <input type="number" step="0.001" placeholder="Units" value={l.qty} onChange={e => setLine(l._k, { qty: e.target.value })} style={mini(68)} />
                  <input type="number" step="0.01" placeholder="Unit $" value={l.unit_price} onChange={e => setLine(l._k, { unit_price: e.target.value })} style={mini(78)} />
                  <button onClick={() => delRow(l._k)} style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 16 }}>×</button>
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={addRow} style={{ marginTop: 4 }}>+ Add line</button>
              {lines.some(l => Number(l.qty) > 0 && !l.sku) && (
                <div style={{ fontSize: 11, color: '#b45309', marginTop: 8 }}>Tip: lines without a SKU still save (units won't map to a product for demand).</div>
              )}
            </div>
            <div style={{ padding: '12px 22px', borderTop: '1px solid #e4e4e7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { setPhase('drop'); setError(''); }}>← Re-upload</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
                <button className="btn btn-primary btn-sm" disabled={busy || !f.customer_id} onClick={save}>Create invoice</button>
              </div>
            </div>
          </>
        )}
      </div>
    </>,
    document.body,
  );
}

function FG({ label, children }) { return <div className="form-group"><label className="form-label">{label}</label>{children}</div>; }
const mini = (w, warn) => ({ fontSize: 12, padding: '5px 8px', border: '1px solid ' + (warn ? '#fca5a5' : '#e4e4e7'), borderRadius: 6, ...(w ? { width: w } : {}) });
